import React from 'react';
import { CalendarIcon, CurrencyDollarIcon, UserIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { HealthTest } from '../../api/healthTests';
import { Bird } from '../../api/birds';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface ViewHealthTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  healthTest: HealthTest | null;
  birds: Bird[];
}

export default function ViewHealthTestModal({
  isOpen,
  onClose,
  healthTest,
  birds,
}: ViewHealthTestModalProps) {
  if (!healthTest) return null;

  const selectedBird = birds.find(bird => bird.id === healthTest.birdId);

  const getResultBadge = (result: string) => {
    const normalResults = ['negative', 'normal', 'within normal limits'];
    const abnormalResults = ['positive', 'abnormal', 'elevated', 'low', 'critical'];
    const pendingResults = ['pending', 'inconclusive'];
    
    const resultLower = result.toLowerCase();
    
    if (normalResults.some(r => resultLower.includes(r))) {
      return <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">{result}</span>;
    }
    if (abnormalResults.some(r => resultLower.includes(r))) {
      return <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">{result}</span>;
    }
    if (pendingResults.some(r => resultLower.includes(r))) {
      return <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">{result}</span>;
    }
    
    return <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">{result}</span>;
  };

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader
        title="Health Test Details"
        subtitle="Complete information for this health test record."
        onClose={onClose}
      />
      <BrandModalBody className="space-y-6">
                      {/* Header Info */}
                      <div className="bg-slate-50/80 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-lg font-medium text-slate-900">{healthTest.testName}</h4>
                            <p className="text-sm text-slate-500">
                              {selectedBird?.bandId} - {selectedBird?.species?.commonName || 'Unknown Species'}
                            </p>
                          </div>
                          <div className="text-right">
                            {healthTest.result && getResultBadge(healthTest.result)}
                          </div>
                        </div>
                      </div>

                      {/* Basic Information */}
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div className="flex items-start space-x-3">
                          <CalendarIcon className="h-5 w-5 text-slate-400 mt-0.5" />
                          <div>
                            <dt className="text-sm font-medium text-slate-500">Test Date</dt>
                            <dd className="mt-1 text-sm text-slate-900">
                              {format(new Date(healthTest.testDate), 'MMMM d, yyyy')}
                            </dd>
                          </div>
                        </div>

                        <div className="flex items-start space-x-3">
                          <UserIcon className="h-5 w-5 text-slate-400 mt-0.5" />
                          <div>
                            <dt className="text-sm font-medium text-slate-500">Tested By</dt>
                            <dd className="mt-1 text-sm text-slate-900">{healthTest.testeeName}</dd>
                          </div>
                        </div>

                        <div className="flex items-start space-x-3">
                          <CurrencyDollarIcon className="h-5 w-5 text-slate-400 mt-0.5" />
                          <div>
                            <dt className="text-sm font-medium text-slate-500">Cost</dt>
                            <dd className="mt-1 text-sm text-slate-900">
                              R{(() => {
                                const cost = typeof healthTest.cost === 'number' ? healthTest.cost : parseFloat(healthTest.cost as any);
                                return !isNaN(cost) ? cost.toFixed(2) : '0.00';
                              })()}
                            </dd>
                          </div>
                        </div>

                        <div className="flex items-start space-x-3">
                          <ClipboardDocumentListIcon className="h-5 w-5 text-slate-400 mt-0.5" />
                          <div>
                            <dt className="text-sm font-medium text-slate-500">Result</dt>
                            <dd className="mt-1 text-sm text-slate-900">
                              {healthTest.result || 'No result recorded'}
                            </dd>
                          </div>
                        </div>
                      </div>

                      {/* Description */}
                      {healthTest.description && (
                        <div>
                          <dt className="text-sm font-medium text-slate-500">Description</dt>
                          <dd className="mt-1 text-sm text-slate-900 whitespace-pre-wrap">
                            {healthTest.description}
                          </dd>
                        </div>
                      )}

                      {/* Comment */}
                      {healthTest.comment && (
                        <div>
                          <dt className="text-sm font-medium text-slate-500">Comment</dt>
                          <dd className="mt-1 text-sm text-slate-900 whitespace-pre-wrap">
                            {healthTest.comment}
                          </dd>
                        </div>
                      )}

                      {/* Notes */}
                      {healthTest.notes && (
                        <div>
                          <dt className="text-sm font-medium text-slate-500">Notes</dt>
                          <dd className="mt-1 text-sm text-slate-900 whitespace-pre-wrap">
                            {healthTest.notes}
                          </dd>
                        </div>
                      )}

                      {/* Documents - Temporarily commented out until database schema is fixed */}
                      {/* {healthTest.documents && healthTest.documents.length > 0 && (
                        <div>
                          <dt className="text-sm font-medium text-slate-500 mb-2">Attached Documents</dt>
                          <dd className="space-y-2">
                            {healthTest.documents.map((doc) => (
                              <div key={doc.id} className="flex items-center space-x-2">
                                <ClipboardDocumentListIcon className="h-4 w-4 text-slate-400" />
                                <a
                                  href={doc.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-cyan-600 hover:text-cyan-500"
                                >
                                  {doc.type}
                                </a>
                                <span className="text-xs text-slate-500">
                                  ({format(new Date(doc.dateAdded), 'MMM d, yyyy')})
                                </span>
                              </div>
                            ))}
                          </dd>
                        </div>
                      )} */}

                      {/* Metadata */}
                      <div className="border-t border-gray-200 pt-4">
                        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <dt className="text-xs font-medium text-slate-500">Created</dt>
                            <dd className="mt-1 text-xs text-slate-900">
                              {format(new Date(healthTest.createdAt), 'MMM d, yyyy h:mm a')}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-slate-500">Last Updated</dt>
                            <dd className="mt-1 text-xs text-slate-900">
                              {format(new Date(healthTest.updatedAt), 'MMM d, yyyy h:mm a')}
                            </dd>
                          </div>
                        </dl>
                      </div>
      </BrandModalBody>
      <BrandModalFooter>
        <button
          type="button"
          className="btn btn-secondary text-sm"
          onClick={onClose}
        >
          Close
        </button>
      </BrandModalFooter>
    </BrandModal>
  );
} 